import React, { useContext, useState, useEffect, useRef } from "react";
/**
 * Purpose: provide seamless transition between 2 content items
 */
import { FunctionComponent } from "react";
import { GqlZoneItemTransition } from "../../../../../store/graphqlTypes";
import { TimelineSlotTransition } from "../TimelineSlotTransition/TimelineSlotTransition";
import {
  e2eTestSlotIdentifier,
  useIsNextItem,
  useShouldRenderForPreloading,
  useTimelineContentVisible,
} from "../utils";
import classnames from "classnames";
import styles from "./TimelineSlot.module.css";
import { timelineSlotPrefix } from "../../../../../test-utils/testIds";
import { isCrossTransition } from "../TimelineSlotTransition/transitionUtils";
import ClientApiContext from "../../../../../ClientApiContext";
import { isLocalDev } from "../../../../../utils/devUtils";
export interface TimelineSlotProps {
  index: number;
  transition: GqlZoneItemTransition | undefined;
  isPreload?: boolean;
  shouldPreloadDynamicContentIn?: number; // Time in ms from current item start when preloading should begin
  isActiveItemTransitioningOut: boolean;
  forceBackground?: boolean;
  forceVisible?: boolean;
  targetTimelineId: string | undefined;
  indexInTimeline: number | undefined;
  isFullscreenActive?: boolean;
  isDynamicContentItem?: boolean;
}

/**
 * Is designed to display content and make transitions between items.
 */
export const TimelineSlot: FunctionComponent<TimelineSlotProps> = (props) => {
  const {
    children,
    transition,
    index,
    isActiveItemTransitioningOut,
    forceBackground,
    targetTimelineId,
    indexInTimeline,
    forceVisible,
    isFullscreenActive,
    shouldPreloadDynamicContentIn,
    isPreload,
    isDynamicContentItem,
  } = props;

  // Local state to manage dynamic content preloading timing
  const [
    shouldPreloadDynamicContent,
    setShouldPreloadDynamicContent,
  ] = useState(false);

  // Ref to store timeout ID for proper cleanup
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Timer logic for dynamic content preloading
  useEffect(() => {
    // Clear any existing timeout first to prevent race conditions
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (
      shouldPreloadDynamicContentIn !== undefined &&
      shouldPreloadDynamicContentIn > 0 &&
      isPreload
    ) {
      // Set timeout to enable preloading at the calculated time
      timeoutRef.current = setTimeout(() => {
        setShouldPreloadDynamicContent(true);
        timeoutRef.current = null; // Clear ref when timeout completes
      }, shouldPreloadDynamicContentIn);
    } else {
      // If no dynamic preload timing is set, reset to false
      setShouldPreloadDynamicContent(false);
    }

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [shouldPreloadDynamicContentIn, isPreload]);

  const clientApiContext = useContext(ClientApiContext);
  const isTizenPlayer = clientApiContext?.playbackConfig?.isTizenPlayer;
  const isNextItem = useIsNextItem(targetTimelineId, indexInTimeline);

  const isCross = transition
    ? isCrossTransition(transition.type, transition.direction)
    : false;

  const shouldRenderForPreloading = useShouldRenderForPreloading(
    targetTimelineId
  );

  const isContentVisible = useTimelineContentVisible({
    isNextItem,
    shouldRenderForPreloading,
    isCrossTransition: isCross,
    isActiveItemTransitioningOut,
  });

  /**
   * Only Tizen need to specific preload css since the style visibility doesn't work properly on tizen
   * it need to use scale to preventing the overlapping video
   */
  const tizenVideoClass = {
    [styles.activeViewer]: isContentVisible && isTizenPlayer,
    [styles.tizenVideoPreload]: !isContentVisible && isTizenPlayer,
    [styles.preloadDevPreview]:
      (!isContentVisible && isLocalDev()) ||
      (isDynamicContentItem && isPreload && isLocalDev()),
  };

  const shouldApplyVisibleClass = isDynamicContentItem
    ? !isPreload
    : isContentVisible;
  const contentClassnames = classnames(styles.base, {
    [styles.visible]: shouldApplyVisibleClass,
    ...tizenVideoClass,
  });

  // For dynamic content preload slots, use shouldPreloadDynamicContent instead of isContentVisible
  const isDynamicContent = shouldPreloadDynamicContentIn !== undefined;
  const shouldShowContent = (() => {
    if (isDynamicContent && isPreload) {
      // For dynamic content preload slots, use our local shouldPreloadDynamicContent state
      return shouldPreloadDynamicContent || forceVisible;
    } else {
      // For regular content, use the normal logic
      return shouldRenderForPreloading || isContentVisible || forceVisible;
    }
  })();

  return (
    <div
      id={isFullscreenActive ? "fullscreen-zone" : undefined}
      className={styles.slotContainer}
    >
      <TimelineSlotTransition
        transition={transition}
        isPreload={props.isPreload ?? false}
        isActiveItemTransitioningOut={isActiveItemTransitioningOut}
        forceBackground={forceBackground}
      >
        <div
          className={contentClassnames}
          id={e2eTestSlotIdentifier(index)}
          aria-label={e2eTestSlotIdentifier(index)}
          data-testid={`${timelineSlotPrefix}${index}`}
        >
          {shouldShowContent ? children : null}
        </div>
      </TimelineSlotTransition>
    </div>
  );
};
